import { ObjectType } from "./objects";

/**
 * The kinds a group's leaves bucket into for bulk editing: placed object types
 * plus the two emitter kinds, which have no ObjectType but expose their own
 * settable params and so form their own bulk sections (Stream = full, Sequencer
 * = sync).
 */
export type BulkKind = ObjectType | "stream" | "sequencer";

/**
 * Per-parameter verdict for a group's same-type descendants: a `uniform` value
 * is bulk-editable, while `mixed` (descendants disagree) and `bound` (at least
 * one descendant drives that param from a setParamIn wire) render as a
 * non-editable dash. `bound` wins over `mixed` — a wired param can never be
 * bulk-overwritten regardless of whether the current values happen to agree.
 */
export type ParamState =
  | { kind: "uniform"; value: number }
  | { kind: "mixed" }
  | { kind: "bound" };

export interface GroupBulkType {
  type: BulkKind;
  count: number;
  params: { name: string; state: ParamState }[];
}

export interface GroupBulkDeps {
  leafIds: (groupId: string) => string[];
  typeOf: (id: string) => BulkKind | null;
  paramNames: (id: string) => string[];
  paramValue: (id: string, name: string) => number;
  isBound: (id: string, name: string) => boolean;
}

export function judgeParam(ids: string[], name: string, deps: GroupBulkDeps): ParamState {
  for (const id of ids) {
    if (deps.isBound(id, name)) {
      return { kind: "bound" };
    }
  }
  const first = deps.paramValue(ids[0], name);
  for (let i = 1; i < ids.length; i++) {
    if (deps.paramValue(ids[i], name) !== first) {
      return { kind: "mixed" };
    }
  }
  return { kind: "uniform", value: first };
}

/**
 * Recursively aggregates a group's leaf descendants by ObjectType (nested groups
 * are flattened via `leafIds`), keeps only types with two or more members, and
 * judges every settable param of each type into uniform / mixed / bound. The
 * panel renders one section per returned type; a single-member type yields no
 * bulk row since there is nothing to reconcile.
 */
export function collectGroupBulkTypes(groupId: string, deps: GroupBulkDeps): GroupBulkType[] {
  const byType = new Map<BulkKind, string[]>();
  for (const id of deps.leafIds(groupId)) {
    const t = deps.typeOf(id);
    if (t === null) {
      continue;
    }
    const arr = byType.get(t);
    if (arr) {
      arr.push(id);
    } else {
      byType.set(t, [id]);
    }
  }
  const out: GroupBulkType[] = [];
  for (const [type, ids] of byType) {
    if (ids.length < 2) {
      continue;
    }
    const params = deps.paramNames(ids[0]).map(
      (name: string): { name: string; state: ParamState } => ({ name, state: judgeParam(ids, name, deps) }),
    );
    out.push({ type, count: ids.length, params });
  }
  return out;
}
